import { useState } from "react";
import { Badge, Button, cn, rowButton } from "../../../ui/index.js";
import { filterRows, type PickerRow } from "./playerCompareView.js";

type Props = {
  title: string;
  rows: PickerRow[];
  selectedId: string | null;
  mappedIds: Map<string, number | null>;
  onSelect: (id: string | null) => void;
  /** The dostats mapping is still loading — the dot is grey, the exact state unknown. */
  mappedLoading?: boolean;
  /** The list is limited to this position (the other side's pick). */
  positionFilter?: string | null;
};

const INPUT =
  "h-8 w-full rounded-md border border-border bg-surface px-2 text-[13px] text-ink " +
  "placeholder:text-ink-faint";

/** One side's list: search, selection (radio semantics), dostats mapping dot. */
export function PlayerPicker({
  title,
  rows,
  selectedId,
  mappedIds,
  onSelect,
  mappedLoading,
  positionFilter,
}: Props) {
  const [query, setQuery] = useState("");
  const [imgError, setImgError] = useState<Set<string>>(new Set());
  const visible = filterRows(rows, query);
  return (
    <div className="flex min-h-0 flex-col gap-2 rounded-lg border border-border bg-surface p-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="flex min-w-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-muted">
          <span className="truncate">{title}</span>
          {positionFilter && <Badge tone="neutral">faqat {positionFilter}</Badge>}
        </h4>
        {selectedId && (
          <Button variant="ghost" size="xs" onClick={() => onSelect(null)}>
            Tozalash
          </Button>
        )}
      </div>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Raqam yoki ism…"
        className={INPUT}
      />
      <ul className="flex max-h-72 flex-col gap-0.5 overflow-y-auto">
        {visible.length === 0 && (
          <li className="px-2 py-3 text-[12px] text-ink-faint">O'yinchi topilmadi</li>
        )}
        {visible.map((r) => {
          const selected = r.id === selectedId;
          const mapped = (mappedIds.get(r.id) ?? null) !== null;
          const showPhoto = r.photoUrl && !imgError.has(r.id);
          const dotTitle = mappedLoading
            ? "Xarita yuklanmoqda…"
            : mapped
              ? "dostats bilan bog'langan"
              : "dostats bilan bog'lanmagan — Xaritalash";
          return (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onSelect(r.id)}
                className={cn(
                  rowButton,
                  "flex items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px]",
                  selected ? "bg-accent/15 text-ink" : "hover:bg-surface-elevated text-ink-soft",
                )}
                title={dotTitle}
              >
                {showPhoto ? (
                  <img
                    src={r.photoUrl ?? undefined}
                    alt=""
                    loading="lazy"
                    onError={() => setImgError((s) => new Set(s).add(r.id))}
                    className="h-7 w-7 shrink-0 rounded-full bg-surface-elevated object-cover"
                  />
                ) : (
                  <span className="h-7 w-7 shrink-0 rounded-full bg-surface-elevated" />
                )}
                <span className="w-7 shrink-0 text-right tabular-nums text-ink-faint">
                  {r.number ?? "—"}
                </span>
                <span className="min-w-0 flex-1 truncate">
                  {r.firstName} <span className="font-semibold">{r.lastName}</span>
                </span>
                {r.position && <Badge tone="neutral">{r.position}</Badge>}
                <span
                  role="img"
                  className={cn(
                    "h-2 w-2 shrink-0 rounded-full",
                    mappedLoading ? "bg-ink-faint" : mapped ? "bg-success" : "bg-warning",
                  )}
                  aria-label={mapped ? "bog'langan" : "bog'lanmagan"}
                />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
